import GlassCard from '@/components/ui/glass-card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { type ProcessedTranscript } from '@/lib/transcript-utils';
import { RecordingStateSelector } from '@/store/selectors';
import { type SimplifiedAppointment } from '@/types/appointment';
import { AnimatePresence, motion } from 'framer-motion';
import { Ear, FileText, FileTextIcon, History, Loader2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

interface AiScribeTranscriptProps {
  selectedAppointment: SimplifiedAppointment | null;
  jobDetails: any;
  aiScribeLoading: boolean;
  aiScribeError: string | null;
  aiScribeJobCompleted: boolean;
  processingStatus?: string;
  estimatedCompletionTime?: string;
  isVisible: boolean;
  onToggleVisibility: () => void;
  scribeJobId?: string; // New: scribeJobId for realtime subscription
  // Existing transcript data props
  processedTranscript?: ProcessedTranscript | null;
  isTranscriptLoading?: boolean;
  existingTranscriptError?: string | null;
  onTranscriptLineClick?: (startTime: number) => void;
  // New: highlighting support
  highlightedSegmentIds?: string[];
  onHighlightSegments?: (ids: string[]) => void; // New prop for clearing highlights
  isLiveTranscriptTyping?: boolean;
  isLiveTranscriptConnected?: boolean;
  liveTranscriptError?: string | null;
  liveTranscriptContent?: string | null;
}

// Transcript Loading Placeholder Component
const TranscriptLoadingPlaceholder = () => {
  return (
    <div
      className="flex items-center justify-center"
      style={{
        minHeight: 'calc(100vh - 25.4rem)',
      }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="text-center max-w-md px-4">
        <div className="rounded-full flex items-center justify-center mx-auto mb-4 border border-white/40 dark:border-white/20 backdrop-blur-sm">
          <Loader2 className="w-20 h-20 text-purple-400 dark:text-purple-500 animate-spin" />
        </div>
        <h3 className="text-slate-700 dark:text-slate-300 font-semibold mb-4 text-2xl font-light">
          Loading transcript...
        </h3>
        <p className="text-slate-500 dark:text-slate-400 text-md">
          Fetching transcript data from the recording. This may take a few
          moments.
        </p>
      </motion.div>
    </div>
  );
};

// Placeholder Component for No Data
const NoDataPlaceholder = () => {
  return (
    <div
      className="flex items-center justify-center"
      style={{
        minHeight: 'calc(100vh - 25.4rem)',
      }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="text-center max-w-md px-4">
        <div className="flex items-center justify-center mx-auto mb-6">
          <FileTextIcon className="w-40 h-40 text-slate-200 dark:text-slate-500" />
        </div>
        <h3 className="text-slate-400 dark:text-slate-300 mb-6 text-2xl font-light">
          No transcript recorded yet
        </h3>
        <p className="text-slate-400 dark:text-slate-400 text-md font-light">
          Start recording to begin live transcription. Your conversation will
          appear here as AI processes the audio in real-time.
        </p>
      </motion.div>
    </div>
  );
};

// Enhanced Status Card Component
const StatusCard = ({
  type,
  title,
  description,
  progress = false,
}: {
  type: 'info' | 'error' | 'success';
  title: string;
  description?: string;
  progress?: boolean;
}) => {
  const variants = {
    info: {
      bg: 'from-eyevantage-primary/10 to-eyevantage-secondary/10',
      border: 'border-eyevantage-primary/30',
      text: 'text-eyevantage-primary dark:text-eyevantage-secondary',
      icon: <Loader2 className="w-4 h-4 animate-spin" />,
    },
    error: {
      bg: 'from-red-500/10 to-red-400/10',
      border: 'border-red-500/30',
      text: 'text-red-600 dark:text-red-400',
      icon: <FileText className="w-4 h-4" />,
    },
    success: {
      bg: 'from-emerald-500/10 to-emerald-400/10',
      border: 'border-emerald-500/30',
      text: 'text-emerald-600 dark:text-emerald-400',
      icon: <FileText className="w-4 h-4" />,
    },
  };

  const variant = variants[type];

  return (
    <div className="px-4 py-3">
      <div
        className={`bg-gradient-to-r ${variant.bg} border ${variant.border} rounded-2xl p-4 backdrop-blur-sm`}>
        <div className="flex items-start gap-3">
          <div className={`${variant.text} flex-shrink-0`}>{variant.icon}</div>
          <div className="flex-1">
            <h3 className={`${variant.text} font-semibold text-sm mb-1`}>
              {title}
            </h3>
            {description && (
              <p
                className={`${variant.text
                  .replace('text-', 'text-')
                  .replace('/600', '/500')
                  .replace('/400', '/300')} text-xs leading-relaxed`}>
                {description}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AiScribeTranscript = React.memo(
  ({
    selectedAppointment,
    jobDetails,
    aiScribeLoading,
    aiScribeError,
    aiScribeJobCompleted,
    processingStatus,
    estimatedCompletionTime,
    isVisible,
    onToggleVisibility,
    scribeJobId,
    processedTranscript,
    isTranscriptLoading = false,
    existingTranscriptError,
    onTranscriptLineClick,
    highlightedSegmentIds,
    onHighlightSegments,
    isLiveTranscriptTyping,
    isLiveTranscriptConnected,
    liveTranscriptError,
    liveTranscriptContent,
  }: AiScribeTranscriptProps) => {
    const isStopped = useSelector(RecordingStateSelector.isStopped);
    const isRecording = useSelector(RecordingStateSelector.isRecording);

    // Tab state
    const [activeTab, setActiveTab] = useState<'transcript' | 'history'>(
      'transcript'
    );

    // Ref for the transcript container to enable auto-scrolling
    const transcriptContainerRef = useRef<HTMLDivElement>(null);
    // Auto-scroll to bottom when new transcript content appears
    useEffect(() => {
      if (
        isLiveTranscriptTyping &&
        transcriptContainerRef.current &&
        !isStopped
      ) {
        const container = transcriptContainerRef.current;

        // Check if user is already near the bottom (within 100px) or if this is the first content
        const isNearBottom =
          container.scrollHeight -
            container.scrollTop -
            container.clientHeight <
          100;

        if (isNearBottom) {
          // Use requestAnimationFrame to ensure DOM is updated before scrolling
          requestAnimationFrame(() => {
            if (transcriptContainerRef.current) {
              transcriptContainerRef.current.scrollTop =
                transcriptContainerRef.current.scrollHeight;
            }
          });
        }
      }
    }, [isLiveTranscriptTyping, liveTranscriptContent]);

    // Auto-scroll to highlighted segments when they change
    useEffect(() => {
      if (
        highlightedSegmentIds &&
        highlightedSegmentIds.length > 0 &&
        processedTranscript?.clickableLines
      ) {
        // Check if transcript is visible
        const transcriptContainer = document.querySelector(
          '[data-transcript-container]'
        );
        if (!transcriptContainer || !isVisible) {
          console.log(
            '🎯 Transcript not visible, cannot scroll to highlighted segments'
          );
          return;
        }

        // Find the first highlighted line
        const firstHighlightedIndex =
          processedTranscript.clickableLines.findIndex((line) => {
            const getSegmentId = (segment: any) => {
              const possibleIds = [
                segment.SegmentId,
                segment.segmentId,
                segment.segment_id,
                segment.id,
                segment.segmentID,
                segment.segmentid,
              ];

              for (const id of possibleIds) {
                if (id != null && id !== '') {
                  return String(id);
                }
              }
              return null;
            };

            return line.segments.some((segment) => {
              const segmentId = getSegmentId(segment);
              if (!segmentId || !highlightedSegmentIds) return false;

              return highlightedSegmentIds.some(
                (highlightedId) =>
                  String(highlightedId).toLowerCase() ===
                  segmentId.toLowerCase()
              );
            });
          });

        if (firstHighlightedIndex !== -1) {
          // Scroll to the highlighted line with a small delay to ensure rendering
          setTimeout(() => {
            const highlightedElements = transcriptContainer.querySelectorAll(
              '[data-highlighted="true"]'
            );
            if (highlightedElements.length > 0) {
              const firstHighlightedElement =
                highlightedElements[0] as HTMLElement;

              // Scroll to the element
              firstHighlightedElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest',
              });
            }
          }, 100);
        }
      }
    }, [
      highlightedSegmentIds,
      processedTranscript?.clickableLines,
      isVisible,
      isStopped,
    ]);

    const renderTranscriptContent = () => {
      // Render mode 1: Live transcript

      if (isRecording) {
        if (!liveTranscriptContent && !isLiveTranscriptTyping) {
          return (
            <div className="flex items-center justify-center h-full">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center space-y-3">
                <div className="relative">
                  {isLiveTranscriptConnected ? (
                    <div className="rounded-full flex items-center justify-center">
                      <Ear className="w-20 h-20 text-purple-400 dark:text-purple-500 animate-pulse" />
                    </div>
                  ) : (
                    <div className="rounded-full flex items-center justify-center">
                      <Loader2 className="w-20 h-20 animate-spin text-purple-400 dark:text-purple-500" />
                    </div>
                  )}
                </div>
                <div className="text-center">
                  <p className="text-slate-700 dark:text-slate-300 font-medium text-2xl font-light">
                    {isLiveTranscriptConnected
                      ? 'Listening for AI transcription...'
                      : 'Connecting...'}
                  </p>
                  <p className="text-slate-500 dark:text-slate-400 text-md mt-1 font-light">
                    {isLiveTranscriptConnected
                      ? 'Real-time transcript will appear here'
                      : 'Establishing real-time connection'}
                  </p>
                </div>
              </motion.div>
            </div>
          );
        }

        return (
          <div className="text-slate-700 dark:text-slate-300 leading-relaxed break-words whitespace-pre-wrap text-md p-2">
            {liveTranscriptContent}
            {isLiveTranscriptTyping && (
              <span className="inline-block w-0.5 h-4 bg-slate-600 dark:bg-slate-400 animate-pulse ml-1 align-middle" />
            )}
          </div>
        );
      }

      // Render mode 2: Final transcript
      if (
        processedTranscript?.clickableLines &&
        processedTranscript.clickableLines.length > 0
      ) {
        return (
          <div className="prose max-w-none">
            {processedTranscript.clickableLines.map((line, index) => {
              const getSegmentId = (segment: any) => {
                return (
                  segment.SegmentId ||
                  segment.segmentId ||
                  segment.segment_id ||
                  segment.id
                );
              };

              const shouldHighlight = line.segments.some((segment) => {
                const segmentId = getSegmentId(segment);
                return highlightedSegmentIds?.includes(segmentId);
              });

              const participantColor =
                line.participant === 'Patient'
                  ? 'text-blue-600'
                  : 'text-slate-800';
              const lineText = line.text.startsWith(line.participant + ': ')
                ? line.text.replace(line.participant + ': ', '')
                : line.text;
              return (
                <div
                  key={index}
                  onClick={() => onTranscriptLineClick(line.startTime)}
                  className={`px-2 text-slate-700 dark:text-slate-300 leading-relaxed break-words text-md m-0 cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-200 rounded-sm transition-all duration-200 ${
                    shouldHighlight
                      ? 'bg-blue-200 dark:bg-blue-900/40 dark:shadow-blue-900/20 font-medium'
                      : ''
                  }`}
                  title={`Click to jump to ${line.startTime}s`}
                  data-highlighted={shouldHighlight ? 'true' : 'false'}>
                  <span className={`font-semibold ${participantColor}`}>
                    {line.participant + ': '}
                  </span>
                  {lineText}
                </div>
              );
            })}
          </div>
        );
      }

      // Fallback for loading states or no data after recording
      if (isTranscriptLoading) return <TranscriptLoadingPlaceholder />;
      if (selectedAppointment) return <NoDataPlaceholder />;

      // Final Fallback: Nothing to render
      return null;
    };

    // History tab content placeholder
    const renderHistoryContent = () => {
      return (
        <div className="flex items-center justify-center h-full">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center max-w-md px-4">
            <div className="flex items-center justify-center mx-auto mb-6">
              <History className="w-40 h-40 text-slate-200 dark:text-slate-500" />
            </div>
            <h3 className="text-slate-400 dark:text-slate-300 mb-6 text-2xl font-light">
              Patient Overview
            </h3>
            <p className="text-slate-400 dark:text-slate-400 text-md font-light">
              Previous transcripts and recordings will appear here.
            </p>
          </motion.div>
        </div>
      );
    };

    const handleTabChange = (tab: string) => {
      setActiveTab(tab as 'transcript' | 'history');
    };

    return (
      <AnimatePresence>
        {isVisible && (
          <motion.div className="w-full flex-shrink-0 mr-0 md:mr-4 relative h-full">
            <GlassCard
              className={`h-full transition-all duration-500 ${
                isRecording
                  ? 'ring-2 ring-red-400/50 dark:ring-red-500/40 shadow-lg shadow-red-500/20 dark:shadow-red-500/10'
                  : ''
              }`}
              variant="elevated">
              <Tabs
                value={activeTab}
                onValueChange={handleTabChange}
                className="flex flex-col h-full">
                {/* Header with Tabs - Similar to image style */}
                <div className="p-2 pt-0 flex items-center justify-center">
                  <TabsList
                    className="
                      flex 
                      w-full 
                      bg-gray-300/80 
                      dark:bg-slate-700/60 
                      backdrop-blur-md 
                      border border-white/30 dark:border-slate-600/40 
                      rounded-full 
                      p-1 mb-3
                      gap-1
                      ">
                    <TabsTrigger
                      value="transcript"
                      className={`flex-1 min-w-0 truncate rounded-full text-md font-medium transition-all duration-300`}>
                      {isRecording ? 'Live Transcript' : 'Transcript'}
                    </TabsTrigger>
                    <TabsTrigger
                      value="history"
                      className={`flex-1 min-w-0 truncate rounded-full text-md font-medium transition-all duration-300`}>
                      PT Overview
                    </TabsTrigger>
                    <TabsTrigger
                      value="aiReport"
                      className={`flex-1 min-w-0 truncate rounded-full text-md font-medium transition-all duration-300`}>
                      AI Report
                    </TabsTrigger>
                  </TabsList>
                </div>
                <TabsContent value="transcript" className="flex-1 mt-0">
                  {/* Status Indicators and Actions */}
                  {(isRecording ||
                    (highlightedSegmentIds &&
                      highlightedSegmentIds.length > 0)) && (
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200/50 dark:border-slate-700/50">
                      <div className="flex items-center gap-3">
                        {isRecording && (
                          <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                            <span className="text-xs font-medium tracking-wide animate-pulse">
                              RECORDING
                            </span>
                          </div>
                        )}
                        {highlightedSegmentIds &&
                          highlightedSegmentIds.length > 0 && (
                            <div className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                              <span className="text-sm font-medium tracking-wide">
                                {highlightedSegmentIds.length} Segment(s)
                                Highlighted
                              </span>
                            </div>
                          )}
                      </div>
                      <div className="flex items-center gap-2">
                        {highlightedSegmentIds &&
                          highlightedSegmentIds.length > 0 && (
                            <button
                              onClick={() => {
                                // Clear highlights by calling the parent's clear function
                                if (onHighlightSegments) {
                                  onHighlightSegments([]);
                                }
                              }}
                              className="relative h-8 px-3 bg-gradient-to-r from-amber-500/85 to-amber-600/85 hover:from-amber-500 hover:to-amber-600 border border-amber-400/40 text-white rounded-2xl text-sm font-medium transition-all duration-200 backdrop-blur-md shadow-sm hover:shadow-md shadow-amber-500/20 flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-amber-500/40 active:scale-[0.98] hover:scale-[1.02] overflow-hidden">
                              {/* Subtle glass layer */}
                              <div className="absolute inset-[1px] rounded-all bg-gradient-to-br from-white/20 via-white/10 to-white/5" />
                              <div className="relative z-10 flex items-center gap-1">
                                <span className="text-xs">✕</span>
                                Clear
                              </div>
                            </button>
                          )}
                      </div>
                    </div>
                  )}
                  <div
                    className="flex flex-col"
                    style={
                      isRecording
                        ? {
                            height: 'calc(100vh - 23.7rem)',
                          }
                        : {
                            height: 'calc(100vh - 21rem)',
                          }
                    }>
                    {/* Status Messages */}
                    <div className="flex-shrink-0">
                      {/* AI Scribe Processing Status */}
                      {isStopped &&
                        !aiScribeJobCompleted &&
                        processingStatus === 'PROCESSING' && (
                          <StatusCard
                            type="info"
                            title="AI is processing your recording..."
                            description={
                              estimatedCompletionTime
                                ? `Estimated completion: ${new Date(
                                    estimatedCompletionTime
                                  ).toLocaleTimeString()}`
                                : undefined
                            }
                            progress
                          />
                        )}

                      {/* AI Scribe Error */}
                      {aiScribeError && (
                        <StatusCard
                          type="error"
                          title="Processing Error"
                          description={aiScribeError}
                        />
                      )}
                    </div>

                    {/* Content */}
                    <div
                      ref={transcriptContainerRef}
                      className="flex-1 overflow-y-auto p-2 pt-0 scroll-smooth"
                      style={{ scrollbarWidth: 'none' }}
                      data-transcript-container>
                      {renderTranscriptContent()}
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="history" className="flex-1 mt-0">
                  <div
                    className="flex flex-col"
                    style={{
                      height: 'calc(100vh - 21rem)',
                    }}>
                    {/* History Content */}
                    <div
                      className="flex-1 overflow-y-auto p-2 scroll-smooth"
                      style={{ scrollbarWidth: 'none' }}>
                      {renderHistoryContent()}
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="aiReport" className="flex-1 mt-0">
                  <div
                    className="flex flex-col"
                    style={{
                      height: 'calc(100vh - 21rem)',
                    }}>
                    {/* AI Report Content */}
                    <div
                      className="flex-1 overflow-y-auto p-2 scroll-smooth"
                      style={{ scrollbarWidth: 'none' }}></div>
                  </div>
                </TabsContent>
              </Tabs>
            </GlassCard>
          </motion.div>
        )}
      </AnimatePresence>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if essential props have changed
    return (
      prevProps.isVisible === nextProps.isVisible &&
      prevProps.scribeJobId === nextProps.scribeJobId &&
      prevProps.selectedAppointment?.appointmentId ===
        nextProps.selectedAppointment?.appointmentId &&
      JSON.stringify(prevProps.processedTranscript) ===
        JSON.stringify(nextProps.processedTranscript) &&
      prevProps.isTranscriptLoading === nextProps.isTranscriptLoading &&
      prevProps.liveTranscriptContent === nextProps.liveTranscriptContent &&
      prevProps.existingTranscriptError === nextProps.existingTranscriptError &&
      prevProps.isLiveTranscriptTyping === nextProps.isLiveTranscriptTyping &&
      prevProps.isLiveTranscriptConnected ===
        nextProps.isLiveTranscriptConnected &&
      prevProps.liveTranscriptError === nextProps.liveTranscriptError &&
      JSON.stringify(prevProps.highlightedSegmentIds) ===
        JSON.stringify(nextProps.highlightedSegmentIds)
    );
  }
);

AiScribeTranscript.displayName = 'AiScribeTranscript';

export default AiScribeTranscript;
